import React, {useEffect, useMemo, useState} from "react";
import * as PropTypes from "prop-types";

export const RE_MODULATION = new RegExp(
  '((allosteric )?inhibit(ors?|i(on|ng))?'
  + '|antagonis([em]|t(s|ic)?)'
  + '|(allosteric )?activat(e|ors?|i(on|ng))?'
  + '|(\\b|_)agonis([em]|t(s|ic)?)'
  + '|(positive|negative).*?modulat(i(on|ng)))',
  "gi");

// PLEX-6698: a promiscuous compound can carry hundreds of evidence entries behind one label
// (a gene set inherits its linked compound's whole activity), so the tooltip shows the first
// few and says how many it left out.
export const ACTIVITY_TOOLTIP_LIMIT = 10;

// PLEX-6697: the results filter searches an activity's evidence too, so a row can match on text
// that only its tooltip shows. markText leaves text it doesn't match unmarked.
const markAsString = (text, markText) => String(markText(String(text ?? ""), {asString: true}));
export const matchesFilter = (text, markText) => (
  !!markText && text != null && text !== "" && markAsString(text, markText).includes("<mark")
);
// One test per label rather than per entry: a label can carry hundreds of entries. A filter
// phrase can't span the separator (looseMatchRegExp never matches across "\n" or "*").
const ENTRY_SEPARATOR = "\n*\n";
const evidenceMatches = (entries, markText) => (
  Array.isArray(entries) && matchesFilter(
    entries.filter(Array.isArray).map(entry => entry.join(" ")).join(ENTRY_SEPARATOR), markText)
);
// [geneSymbol, {activityLabel: [[title, desc], ...]}] or, evidence-stripped, [geneSymbol, [label, ...]]
export const activityMatches = ([symbol, activity], markText) => {
  if (!markText) {
    return false;
  }
  if (matchesFilter(symbol, markText)) {
    return true;
  }
  if (Array.isArray(activity)) {
    return activity.some(label => matchesFilter(label, markText));
  }
  return Object.entries(activity || {}).some(([label, entries]) => (
    matchesFilter(label, markText) || evidenceMatches(entries, markText)));
};
export const activitiesListMatches = (list, markText) => (
  !!markText && list.some(el => activityMatches(el, markText))
);

// Must return a string
export const formatActivitiesTooltip = (entries, markText = null, limit = ACTIVITY_TOOLTIP_LIMIT) => {
  if (!Array.isArray(entries)) {
    // Malformed activity data (label mapped to something other than [[title, desc], ...])
    // must not throw here: an uncaught error while building the tooltip gets caught by the
    // MarkdownRenderError boundary, which re-renders and hits the same throw again --
    // a tight crash loop that floods Sentry and hangs the tab (WEBAPP-EC/WEBAPP-ED).
    console.warn("formatActivitiesTooltip: expected an array of [title, desc] pairs", entries);
    return "<ul class='activities-tooltip' ></ul>";
  }
  // Entries that match the results filter come first, marked, so the limit never hides them
  const rows = entries.map(([title, desc]) => {
    const [mtitle, mdesc] = markText ? [markAsString(title, markText), markAsString(desc, markText)] : [title, desc];
    return {item: `<em>${mtitle}</em> ${mdesc}`, matched: !!markText && `${mtitle} ${mdesc}`.includes("<mark")};
  });
  const ordered = [...rows.filter(row => row.matched), ...rows.filter(row => !row.matched)];
  const listItems = ordered.slice(0, limit).reduce((result, {item}) => (
    result + `<li class='activity-entry'>${item.replaceAll(RE_MODULATION, '<b>$1</b>')}</li>`
  ), "");
  const more = entries.length > limit
    ? `<li class='activity-more'><em>and ${entries.length - limit} more</em></li>`
    : "";
  return `<ul class='activities-tooltip' >${listItems}${more}</ul>`;
}

// Special-case activities formatting
// list of elements of the form [geneSymbol, {activityLabel: [title, desc], ...}]
export const isActivitiesList = (list) => list.length > 0 && typeof(list[0]) !== "string";

export const formatActivitiesList = (list, markText = null) =>
  list.map((el, idx) => formatActivity(el, idx, markText));

const Activity = ({label, entries, index, markText = null}) => {
  // PLEX-6698: build the tooltip when the label is first hovered, not when the row renders.
  // A results page has hundreds of labels and most are never hovered; building every
  // tooltip up front is what made gene set rows slow (PLEX-6441).
  const [tooltipHTML, setTooltipHTML] = useState("");

  // New data or a new filter needs a new tooltip, built on the next hover.
  useEffect(() => setTooltipHTML(""), [entries, markText]);

  const buildTooltip = (event) => {
    if (tooltipHTML) {
      return;
    }
    const html = formatActivitiesTooltip(entries, markText);
    // Set the attribute right away as well: the shared tooltip opens on this same mouseover
    // and reads it before React re-renders with the new state (it also watches the attribute).
    event.currentTarget.setAttribute("data-tooltip-html", html);
    setTooltipHTML(html);
  };

  // PLEX-6697: a match only in the evidence marks the whole label, since the label is what
  // the user can see and hover.
  const match = useMemo(() => (
    matchesFilter(label, markText) ? "label" : evidenceMatches(entries, markText) ? "evidence" : null
  ), [label, entries, markText]);
  const shownLabel = match === "label" ? markText(label) : match === "evidence" ? (<mark>{label}</mark>) : label;

  return (
    <span
      className="specific-activity"
      key={index}
      data-tooltip-id="tt"
      data-tooltip-html={tooltipHTML || undefined}
      onMouseEnter={buildTooltip}
    >
      {index !== 0 ? ", " : ""}{shownLabel}
    </span>
  );
};


export const formatActivity = ([symbol, activity], idx, markText = null) => {
  try {
    // Evidence-stripped shape (noevidence filter): gene_symbol -> [label1, label2, ...],
    // no per-label evidence to build a tooltip from -- just render the label text.
    const activities = Array.isArray(activity)
      ? activity.map((label, label_idx) => (
          <span className="specific-activity" key={label_idx}>
            {label_idx !== 0 ? ", " : ""}{markText ? markText(label) : label}
          </span>
        ))
      : Object.entries(activity)
          .map(([label, entries], label_idx) => (
            <Activity label={label} entries={entries} index={label_idx} key={label_idx} markText={markText} />
          ));
    // markText hands back text it doesn't match unmarked, so only a match replaces the bold symbol
    const marked = matchesFilter(symbol, markText) ? markText(symbol) : (<b>{symbol}</b>);
    return (<span className="target-activity" key={idx}>{marked} {activities}</span>);

  } catch(error) {
    console.warn(`Error decoding activities for ${symbol}`, error);
  }
  return (<span className="target-activity target-activity-truncated" key={idx}>{symbol}</span>);
};

const VisiblePart = ({ list, markText = null, showEllipsis = false }) => {

  const formatList = (list, markText = null) => {
    if (isActivitiesList(list)) {
      return formatActivitiesList(list, markText);
    }
    return markText ? list.map(el => markText(el)) : list;
  };
  const [items, setItems] = useState(formatList(list, markText));
  const isActivities = isActivitiesList(list);

  useEffect(() => {
    const unmarkedItems = formatList(list);
    if (markText) {
      const markedItems = formatList(list, markText);
      const hasMarked = !_.isEqual(unmarkedItems, markedItems)
      setItems(hasMarked ? (_.isEqual(items, markedItems) ? items : markedItems) : unmarkedItems);
    }
    else {
      setItems(_.isEqual(items, unmarkedItems) ? items : unmarkedItems);
    }
  }, [list, markText]);

  return (
    // symbol => (label=>desc)
    <span className={`collapsible-visible${isActivities && " activities" || ""}`}>{
      [...items, ...(showEllipsis ? [showEllipsis] : [])].map((el, item_idx) => [
        (item_idx !== 0
          ? (<span key={`div-${item_idx}`} className="list-divider">|</span>)
          : null),
        (<span className="collapsible-visible-item" key={item_idx}>{el}</span>),
        " "
      ])}</span>
  );
}

VisiblePart.propTypes = {
  list: PropTypes.array.isRequired,
  markText: PropTypes.func,
};

export default VisiblePart;
